import { SelectionChangeResponse, Tree, TreeSearch, TreeSummary, TypeCounter } from "@rio-cloud/rio-uikit";
import { get, isEmpty } from "lodash";
import { useMemo, useState } from "react";
import { IFilterOptions } from "../useAssetTree";
import { useT } from "@hooks";

interface IClientSupplierTree {
  filterOptions: IFilterOptions;
  selectedClients: string[];
  selectedSuppliers: string[];
  setSelectedClients: (items: string[]) => void;
  setSelectedSuppliers: (items: string[]) => void;
  includeNullClient: boolean;
  includeNullSupplier: boolean;
  setIncludeNullClient: (value: boolean) => void;
  setIncludeNullSupplier: (value: boolean) => void;
}

const CLIENT_GROUP_ID = "clients";
const SUPPLIER_GROUP_ID = "suppliers";
const DESAGRUPADO_GROUP_ID = "__desagrupado__";
const DESAGRUPADO_ITEM_ID = "__desagrupado_client_supplier__";

const ClientSupplierTree = ({
  filterOptions,
  selectedClients,
  selectedSuppliers,
  setSelectedClients,
  setSelectedSuppliers,
  includeNullClient,
  includeNullSupplier,
  setIncludeNullClient,
  setIncludeNullSupplier,
}: IClientSupplierTree) => {
  const t = useT();
  const [searchValue, setSearchValue] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<string[]>([
    CLIENT_GROUP_ID,
    SUPPLIER_GROUP_ID,
    DESAGRUPADO_GROUP_ID,
  ]);

  const groups = useMemo(
    () => [
      { id: CLIENT_GROUP_ID, name: t("assetTree.clients.group") },
      { id: SUPPLIER_GROUP_ID, name: t("assetTree.suppliers.group") },
      { id: DESAGRUPADO_GROUP_ID, name: t("assetTree.ungrouped") },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  const items = useMemo(() => {
    const clients =
      filterOptions?.clients.map((client) => ({
        id: client.id,
        name: client.label,
        type: "shopping-cart",
        groupIds: [CLIENT_GROUP_ID],
      })) || [];

    const suppliers =
      filterOptions?.suppliers.map((supplier) => ({
        id: supplier.id,
        name: supplier.label,
        type: "order",
        groupIds: [SUPPLIER_GROUP_ID],
      })) || [];

    const desagrupadoItem = {
      id: DESAGRUPADO_ITEM_ID,
      name: t("assetTree.clientSupplier.noItem"),
      type: "filter",
      groupIds: [DESAGRUPADO_GROUP_ID],
    };

    return [...clients, ...suppliers, desagrupadoItem];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOptions?.clients, filterOptions?.suppliers, t]);

  const filteredItems = useMemo(
    () => items.filter((item) => get(item, "name", "").toLowerCase().includes(searchValue.toLowerCase())),
    [items, searchValue]
  );

  const filteredGroups = useMemo(() => {
    const availableGroupIds = new Set<string>();
    filteredItems.forEach((item) => item.groupIds?.forEach((groupId) => availableGroupIds.add(groupId)));
    return groups.filter((group) => availableGroupIds.has(group.id));
  }, [filteredItems, groups]);

  const itemTypeById = useMemo(() => {
    const map = new Map<string, "client" | "supplier">();
    filterOptions?.clients.forEach((client) => map.set(client.id, "client"));
    filterOptions?.suppliers.forEach((supplier) => map.set(supplier.id, "supplier"));
    return map;
  }, [filterOptions?.clients, filterOptions?.suppliers]);

  const groupItemIds = useMemo(() => {
    const clientIds = filterOptions?.clients.map((client) => client.id) || [];
    const supplierIds = filterOptions?.suppliers.map((supplier) => supplier.id) || [];
    return {
      [CLIENT_GROUP_ID]: clientIds,
      [SUPPLIER_GROUP_ID]: supplierIds,
      [DESAGRUPADO_GROUP_ID]: [DESAGRUPADO_ITEM_ID],
    };
  }, [filterOptions?.clients, filterOptions?.suppliers]);

  const selectedGroups = useMemo(() => {
    const selectedClientSet = new Set(selectedClients);
    const selectedSupplierSet = new Set(selectedSuppliers);
    const groupsSelected: string[] = [];

    if (
      groupItemIds[CLIENT_GROUP_ID]?.length &&
      groupItemIds[CLIENT_GROUP_ID].every((id) => selectedClientSet.has(id))
    ) {
      groupsSelected.push(CLIENT_GROUP_ID);
    }
    if (
      groupItemIds[SUPPLIER_GROUP_ID]?.length &&
      groupItemIds[SUPPLIER_GROUP_ID].every((id) => selectedSupplierSet.has(id))
    ) {
      groupsSelected.push(SUPPLIER_GROUP_ID);
    }
    if (includeNullClient && includeNullSupplier) {
      groupsSelected.push(DESAGRUPADO_GROUP_ID);
    }

    return groupsSelected;
  }, [groupItemIds, selectedClients, selectedSuppliers, includeNullClient, includeNullSupplier]);

  const handleSelectItems = ({ items: selectedItemIds, groups: selectedGroupIds }: SelectionChangeResponse) => {
    const nextSelectedIds = new Set<string>(selectedItemIds);
    const nextGroups = new Set<string>(selectedGroupIds || []);

    (selectedGroupIds || []).forEach((groupId) => {
      const ids = groupItemIds[groupId as keyof typeof groupItemIds] || [];
      ids.forEach((id) => nextSelectedIds.add(id));
    });

    const desagrupadoSelected = nextGroups.has(DESAGRUPADO_GROUP_ID) || nextSelectedIds.has(DESAGRUPADO_ITEM_ID);
    setIncludeNullClient(desagrupadoSelected);
    setIncludeNullSupplier(desagrupadoSelected);

    nextSelectedIds.delete(DESAGRUPADO_ITEM_ID);

    const nextClients: string[] = [];
    const nextSuppliers: string[] = [];

    nextSelectedIds.forEach((itemId) => {
      const type = itemTypeById.get(itemId);
      if (type === "client") nextClients.push(itemId);
      if (type === "supplier") nextSuppliers.push(itemId);
    });

    setSelectedClients(nextClients);
    setSelectedSuppliers(nextSuppliers);
  };

  const selectedClientCount = selectedClients.length;
  const selectedSupplierCount = selectedSuppliers.length;

  const handleUpdateSearch = (value: string) => setSearchValue(value);

  return (
    <Tree
      groups={filteredGroups}
      items={filteredItems}
      expandedGroups={expandedGroups}
      onExpandGroupsChange={setExpandedGroups}
      selectedGroups={selectedGroups}
      selectedItems={
        includeNullClient && includeNullSupplier
          ? [...selectedClients, ...selectedSuppliers, DESAGRUPADO_ITEM_ID]
          : [...selectedClients, ...selectedSuppliers]
      }
      onSelectionChange={handleSelectItems}
      hasMultiselect={!isEmpty(filteredItems)}
      summary={
        <TreeSummary>
          <TypeCounter icon="rioglyph rioglyph-shopping-cart" value={selectedClientCount} />
          <TypeCounter icon="rioglyph rioglyph-order" value={selectedSupplierCount} />
        </TreeSummary>
      }
      search={
        <TreeSearch
          value={searchValue}
          placeholder={t("assetTree.clientSupplier.search")}
          onChange={handleUpdateSearch}
        />
      }
    />
  );
};

export default ClientSupplierTree;
